import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { BrandMark } from "@/components/BrandMark";

const SECTIONS = [
  {
    title: "Private by default",
    body: "Memories Forever is not a public profile and cannot be found by searching. Your family's story is visible to you when you sign in, and to the people you invite through their own private link.",
  },
  {
    title: "Private invitations",
    body: "Each family member is invited through a unique, unguessable private link — not a public account. Anyone holding that link can open that one invitation page, so please share it only with the person it's meant for.",
  },
  {
    title: "Consent before sharing",
    body: "When someone records a reply, they confirm that they're happy for their recording to be shared privately with the family before it is sent. Nothing is submitted without that confirmation.",
  },
  {
    title: "How recordings are used",
    body: "Recordings are stored privately so they can be played back and joined, in the order you choose, into your original audio keepsake. The original keepsake uses only your family's original recordings — they are not rewritten or replaced with synthetic speech. The optional narrated-story upgrade (coming soon) will use AI-recreated voices only with each speaker's own explicit consent; paying for the upgrade is not consent on anyone else's behalf.",
  },
  {
    title: "How the technology is used",
    body: "Memories Forever is designed not to invent events, quotations, dates, relationships, emotions, conversations or memories. AI helps transcribe and organise what your family provides; if information is missing it is left out or brought back to you to confirm, and you review and approve your story before it is created. The family's real recordings always remain the story.",
  },
  {
    title: "Your control",
    body: "As the person who created your family's Memories Forever space, you can remove any family member's chapter — which deletes their recordings from that story — and you can download your finished story as a single audio file to keep.",
  },
];

export default function Privacy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#C9C2D8] mb-8" data-testid="privacy-back-btn">
          <ArrowLeft className="h-5 w-5" /> Back
        </button>

        <div className="text-center mb-12">
          <div className="flex justify-center mb-5"><BrandMark size={44} /></div>
          <p className="eyebrow mb-3">Privacy &amp; your control</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#EDE6F7] leading-tight">Your voice stays your voice.</h1>
          <p className="mt-5 text-lg text-[#C9C2D8] leading-relaxed">
            Voice recordings are deeply personal. Here is how they are treated and what you can control.
          </p>
        </div>

        <div className="space-y-5">
          {SECTIONS.map((s, i) => (
            <section key={i} className="glass-card p-6 sm:p-8" data-testid={`privacy-section-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="h-5 w-5 text-[#B57BFF]" />
                <h2 className="font-serif text-2xl text-[#EDE6F7]">{s.title}</h2>
              </div>
              <p className="text-[#C9C2D8] text-lg leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>

        <p className="text-sm text-[#8C84A0] mt-10 leading-relaxed">
          This page describes how Memories Forever works today and will grow as the product does. If you'd ever like a
          recording removed, please ask the family member who invited you to Memories Forever.
        </p>
      </main>
    </div>
  );
}
